'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';

import Arrow from 'assets/icons/arrow_back.svg';

import Modal from './Modal';

export default function Lightbox({ images, activeIndex, close }) {
  const isOpen = activeIndex !== null;
  const total = images.length;

  // ------------------------------
  // EMBLA CAROUSEL
  // ------------------------------
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: total > 1, align: 'center' });

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi || !isOpen || activeIndex === null) return;
    // Reset embla completely
    emblaApi.reInit();
    // Jump directly, no animation, no flash
    emblaApi.scrollTo(activeIndex, true);

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, activeIndex, isOpen]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollToIndex = (i) => emblaApi?.scrollTo(i);

  if (!isOpen) return null;

  // DOTS RANGE (5 at a time)
  const MAX_DOTS = 5;
  let start = Math.max(0, selectedIndex - 2);
  let end = Math.min(total - 1, start + MAX_DOTS - 1);

  if (end - start + 1 < MAX_DOTS) {
    start = Math.max(0, end - (MAX_DOTS - 1));
  }

  const visibleDots = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      className="w-full h-full max-w-full! bg-transparent! flex"
      ModalBodyClass="m-auto"
      useMobileSlide={false}
    >
      {/* BACKDROP */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        onClick={(e) => {
          if (!e.target.closest('[data-safe-zone]')) {
            close();
          }
        }}
      >
        {/* EMBLA CAROUSEL */}
        <div className="embla overflow-hidden w-full rounded-xl" ref={emblaRef}>
          <div className="embla__container flex touch-pan-y">
            {images.map((src, i) => (
              <div
                key={i}
                className="embla__slide flex-[0_0_100%] relative flex items-center justify-center"
              >
                <div className="flex items-center justify-center w-full h-full max-h-[calc(100vh-200px)] rounded-xl overflow-hidden">
                  <span className="rounded-xl overflow-hidden h-full flex items-center justify-center">
                    <Image
                      data-safe-zone
                      src={src}
                      alt={`photo-${i}`}
                      width={1500}
                      height={1000}
                      className="object-contain w-full h-full max-w-min max-h-min rounded-xl"
                    />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {total > 1 && (
          <div
            data-safe-zone
            className="absolute bottom-4 flex items-center gap-4 left-0 right-0 mx-auto sm:w-fit w-full sm:px-0 px-2 sm:justify-center justify-between"
          >
            <button
              type="button"
              onClick={scrollPrev}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <Image src={Arrow} alt="prev" className="rotate-180" />
            </button>
            <div className="flex items-center gap-3">
              {visibleDots.map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollToIndex(i)}
                  className={`lg:h-2 lg:w-2 w-1.5 h-1.5 rounded-full cursor-pointer transition-all ${
                    i === selectedIndex
                      ? 'bg-white! w-4! lg:w-6!'
                      : 'lg:bg-white/70 bg-white/60 hover:bg-white/90'
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={scrollNext}
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <Image src={Arrow} alt="next" />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
